'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
  {
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12cb5c9f5-1767670205545.png",
    alt: 'Athletic young man in black sportswear sprinting on outdoor track with intense focus and determination',
    title: 'Elite Training. Inclusive Approach.',
    subtitle: 'Where Olympic-level methodology meets everyday fitness goals'
  },
  {
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_129733409-1764779896324.png",
    alt: 'Group of diverse athletes doing strength training exercises with dumbbells in modern gym facility',
    title: 'Building Character Through Discipline',
    subtitle: 'Structured progression for lifelong fitness practice'
  },
  {
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_160b59d41-1768230266473.png",
    alt: 'Young children in colorful athletic wear participating in speed training drills on sports field',
    title: 'Foundation Programs for Future Champions',
    subtitle: 'Age-specific development from grassroots to excellence'
  },
  {
    image: "/assets/images/PHOTO-2024-08-03-10-39-41-1769030756632.jpg",
    alt: 'Athletes performing hurdle drills during training session at GO Speed academy',
    title: 'Professional Training Methodology',
    subtitle: 'Expert coaching with proven techniques for athletic excellence'
  },
  {
    image: "/assets/images/PHOTO-2024-08-03-10-39-40_2-1769952351844.jpg",
    alt: 'Athletes performing structured circle agility drills with hurdles at GO Speed training facility',
    title: 'Precision Agility Training',
    subtitle: 'Advanced drills for speed, coordination, and athletic performance'
  }];


  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  return (
    <section className={`relative w-full h-[600px] lg:h-[700px] overflow-hidden ${className}`}>
      {heroSlides.map((slide, index) =>
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        currentSlide === index ? 'opacity-100' : 'opacity-0'}`
        }>

          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent z-10" />
          <AppImage
          src={slide.image}
          alt={slide.alt}
          className="w-full h-full object-cover"
          priority={index === 0} />

        </div>
      )}

      <div className="relative z-20 h-full flex items-center">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              {heroSlides[currentSlide].title}
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-8 font-body">
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://forms.gle/9yvnUPvV2ifJin4S9"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-theme-orange text-white font-heading font-bold text-lg rounded-md shadow-hover hover:shadow-modal hover:bg-theme-orange/90 transition-all duration-300 transform hover:-translate-y-1 text-center">

                Book Your Trial Session
              </a>
              <Link
                href="/programs-overview"
                className="px-8 py-4 bg-white text-theme-black font-heading font-semibold text-lg rounded-md shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1 text-center">

                Explore Programs
              </Link>
            </div>
          </div>
        </div>
      </div>

      {isHydrated &&
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, index) =>
        <button
          key={index}
          onClick={() => setCurrentSlide(index)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
          currentSlide === index ? 'bg-accent w-8' : 'bg-white/50 hover:bg-white/80'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
        </div>
      }
    </section>);

};

export default HeroSection;